import React, { useEffect, useState } from 'react';
import { marketing } from '../services/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Bell, Loader2, Mail, MessageCircle, Clock } from 'lucide-react';

// Compact profile settings card: (1) Deal alert delivery mode
// (instant vs evening roundup) and (2) per-trigger opt-out toggles for
// email + SMS across every auto-responder trigger.
export default function NotificationPrefsCard() {
  const { user, refresh } = useAuth();
  const [rows, setRows] = useState(null);
  const [mode, setMode] = useState(user?.notificationPrefs?.dealAlerts || 'instant');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await marketing.getMyAutoResponderPrefs();
      setRows(r?.triggers || []);
    } catch { setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const setDealMode = async (nextMode) => {
    setMode(nextMode); setSaving(true);
    try {
      await marketing.setDealAlertMode(nextMode);
      await refresh?.();
      toast({ title: nextMode === 'instant' ? 'Instant alerts on' : 'Evening roundup on' });
    } catch (e) {
      setMode(mode);
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  const togglePref = async (key, channel, wasOptedOut) => {
    const nextOptedOut = !wasOptedOut;
    setRows(rows.map(r => r.key === key ? { ...r, [`${channel}OptedOut`]: nextOptedOut } : r));
    try { await marketing.patchMyAutoResponderPref(key, channel, nextOptedOut); }
    catch (e) {
      setRows(rows);
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '' });
    }
  };

  return (
    <div className="pbb-card p-5 space-y-4" data-testid="notif-prefs-card">
      <div className="flex items-center gap-2">
        <Bell size={16} className="text-green-400"/>
        <div className="text-white font-semibold">Notifications</div>
      </div>

      {/* Deal alert delivery mode */}
      <div>
        <div className="text-xs text-gray-400 mb-2 flex items-center gap-1"><Clock size={12}/> Deal alerts in my city</div>
        <div className="flex gap-2">
          <button data-testid="deal-alert-mode-instant" onClick={() => setDealMode('instant')} disabled={saving}
                  className={`flex-1 px-3 py-2 rounded border text-sm ${mode==='instant' ? 'border-green-500 bg-green-500/10 text-green-300' : 'border-gray-700 text-gray-300 hover:bg-gray-800'}`}>
            Instant alerts
          </button>
          <button data-testid="deal-alert-mode-evening" onClick={() => setDealMode('evening')} disabled={saving}
                  className={`flex-1 px-3 py-2 rounded border text-sm ${mode==='evening' ? 'border-green-500 bg-green-500/10 text-green-300' : 'border-gray-700 text-gray-300 hover:bg-gray-800'}`}>
            Evening roundup (7 PM)
          </button>
        </div>
      </div>

      {/* Per-trigger SMS opt-out */}
      <div>
        <div className="text-xs text-gray-400 mb-2 flex items-center gap-1"><MessageCircle size={12}/> Text messages (SMS)</div>
        {rows === null ? (
          <div className="text-gray-500 text-xs inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin"/> Loading…</div>
        ) : (
          <div className="max-h-64 overflow-y-auto pr-1 space-y-1">
            {rows.map(r => (
              <label key={`sms-${r.key}`} className="flex items-center gap-2 text-sm text-white cursor-pointer py-1">
                <input data-testid={`pref-sms-${r.key}`} type="checkbox" checked={!r.smsOptedOut}
                        onChange={() => togglePref(r.key, 'sms', r.smsOptedOut)}/>
                <span className="text-xs text-gray-300 flex-1 min-w-0 truncate">{r.label}</span>
                {r.transactional && <span className="text-[9px] text-blue-300 border border-blue-500/40 rounded-full px-1.5 py-[1px]">Transactional</span>}
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
